import { useMemo } from "react";
import { Box, Chip } from "@mui/material";
import { FastField } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PhoneInput from "./PhoneInput";
import SelectContactButton from "../../../components/SelectContactButton";
import getReference from "../../../utils/getReference";
import { formatPhoneToPlain } from "../../../utils/functions";
import { selectContacts } from "../../../redux/selectors/contact.selector";
import {
  selectDevices,
  selectV4OnlyDevices,
} from "../../../redux/selectors/device.selector";
import { ContactIcon, DeviceIcon } from "../../../utils/icons";
import { humanizeSettingKey } from "../utils";

// Menu call-number phone: pick a contact/device (stored as a {{c-id}}/{{d-id}}
// reference) or type a plain number. Devices are scoped to V4 only.
export default function AddressBookPhoneInput({
  id,
  name,
  onChange,
  disabled,
  locked,
}) {
  const { t } = useTranslation();
  const contacts = useSelector(selectContacts());
  const devices = useSelector(selectDevices());
  const v4Devices = useSelector(selectV4OnlyDevices());

  // A merged device is V4 if it matches a V4-store entry by uuid/id/uniqueId
  // (the imei-led dedup keys); keeps the numeric id for the {{d-id}} reference.
  const isV4Device = useMemo(() => {
    const keys = new Set();
    v4Devices.forEach((d) => {
      if (d.uuid) keys.add(`uuid:${d.uuid}`);
      if (d.id) keys.add(`id:${d.id}`);
      if (d.uniqueId) keys.add(`uid:${d.uniqueId}`);
    });
    return (d) =>
      (d.uuid && keys.has(`uuid:${d.uuid}`)) ||
      (d.id && keys.has(`id:${d.id}`)) ||
      (d.uniqueId && keys.has(`uid:${d.uniqueId}`));
  }, [v4Devices]);

  return (
    <FastField name={id}>
      {({ field, form }) => {
        const value = field.value || "";
        const ref = getReference(value);
        const refName = !ref
          ? null
          : ref.contact != null
          ? contacts?.find?.((c) => c.id === ref.contact)?.name
          : devices?.find?.((d) => d.id === ref.device)?.name;
        const set = (v) => {
          form.setFieldValue(field.name, v);
          onChange?.(field.name, v);
        };

        return (
          <Box sx={{ py: 0.75, px: 2 }}>
            {ref ? (
              <Chip
                variant="outlined"
                icon={
                  ref.contact != null ? (
                    <ContactIcon size={0.7} />
                  ) : (
                    <DeviceIcon size={0.7} />
                  )
                }
                label={refName || t("unknown", { defaultValue: "Unknown" })}
                onDelete={disabled ? undefined : () => set("")}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                  <PhoneInput
                    value={value}
                    label={t(name, { defaultValue: humanizeSettingKey(name) })}
                    disabled={locked}
                    onChange={(v) => form.setFieldValue(field.name, v)}
                    onBlur={() => onChange?.(field.name, field.value)}
                  />
                </Box>
                {!disabled && (
                  <SelectContactButton
                    subtitle={(c) => formatPhoneToPlain(c.phone, true)}
                    deviceFilter={isV4Device}
                    onContactSelected={(c) => set(`{{c-${c.id}}}`)}
                    onDeviceSelected={(d) => set(`{{d-${d.id}}}`)}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      }}
    </FastField>
  );
}
